/**
 * Bridge Option B: Tiger (mobile WebView) guarda/desbloquea password|refresh en Keystore/Keychain.
 * Alcance: solo canal Tiger (`isTigerEnvironment` = Android/iOS bridge o `?tigerSim=1`).
 * No aplica al login web del tenant.
 *
 * Contrato nativo (Android window.Jph / iOS JphIOS postMessage):
 *   tigerSaveBiometricCredential(email, secretType, secret)
 *   tigerUnlockBiometricCredential(email)  // biometría OS + lectura Keystore
 *   tigerClearBiometricCredential(email)
 *   tigerHasBiometricCredential(email)     // opcional
 *
 * Callbacks WebView:
 *   tigerResultBiometricCredentialSave(ok, description?, errorCode?)
 *   tigerResultBiometricCredential(ok, payload)  // unlock
 *   tigerResultBiometricCredentialClear(ok, description?, errorCode?)
 *   tigerResultBiometricCredentialHas(ok, has, description?, errorCode?)
 *
 * tigerSim (lab): almacén sessionStorage — solo con ?tigerSim=1, nunca en Tiger real.
 */
import { isTigerAndroid, isTigerIOS, isTigerEnvironment, isTigerSimActive } from '../tigerDetector';
import {
  BRIDGE_CODES,
  MAX_SECRET_LEN,
  SECRET_TYPES,
  SECRET_TYPE_PASSWORD,
  SECRET_TYPE_REFRESH,
  assertUnlockForLogin,
  isValidEmailShape,
  normalizeEmail,
  normalizeSecretType,
  parseUnlockResult,
  redactSecretsInMessage,
} from './tigerBiometricCredential.contract';

const TIMEOUT_MS = 60_000;
const SIM_STORE_KEY = 'wms_tiger_bio_cred_sim_v1';

let bridgeInitialized = false;

/** @type {Array<(result: object) => void>} */
let unlockQueue = [];
/** @type {Array<(result: { ok: boolean, description: string, errorCode: * }) => void>} */
let saveQueue = [];
/** @type {Array<(result: { ok: boolean, description: string, errorCode: * }) => void>} */
let clearQueue = [];
/** @type {Array<(result: { ok: boolean, has: boolean }) => void>} */
let hasQueue = [];

function readSimStore() {
  try {
    const raw = sessionStorage.getItem(SIM_STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeSimStore(store) {
  try {
    sessionStorage.setItem(SIM_STORE_KEY, JSON.stringify(store));
  } catch {
    /* private mode */
  }
}

function settleAll(queue, result) {
  const pending = queue.splice(0, queue.length);
  pending.forEach((resolve) => {
    try {
      resolve(result);
    } catch {
      /* ignore */
    }
  });
}

/**
 * @returns {boolean}
 */
export function isTigerBiometricCredentialBridgeAvailable() {
  if (typeof window === 'undefined') return false;
  if (isTigerSimActive()) return true;
  if (isTigerAndroid()) {
    const b = window.Jph;
    return Boolean(
      b
      && typeof b.tigerSaveBiometricCredential === 'function'
      && typeof b.tigerUnlockBiometricCredential === 'function'
      && typeof b.tigerClearBiometricCredential === 'function'
    );
  }
  if (isTigerIOS()) {
    const handler = window.webkit?.messageHandlers?.JphIOS;
    return Boolean(handler && typeof handler.postMessage === 'function');
  }
  return false;
}

export function ensureTigerBiometricCredentialBridge() {
  if (bridgeInitialized || typeof window === 'undefined') return;
  bridgeInitialized = true;

  window.tigerResultBiometricCredentialSave = (ok, description, errorCode) => {
    settleAll(saveQueue, {
      ok: Boolean(ok),
      description: String(description ?? '').trim(),
      errorCode: errorCode ?? null,
    });
  };

  window.tigerResultBiometricCredentialClear = (ok, description, errorCode) => {
    settleAll(clearQueue, {
      ok: Boolean(ok),
      description: String(description ?? '').trim(),
      errorCode: errorCode ?? null,
    });
  };

  window.tigerResultBiometricCredentialHas = (ok, has, description, errorCode) => {
    settleAll(hasQueue, {
      ok: Boolean(ok),
      has: Boolean(has),
      description: String(description ?? '').trim(),
      errorCode: errorCode ?? null,
    });
  };

  /**
   * Unlock callback. Prefer payload object/JSON; nunca loguear.
   */
  window.tigerResultBiometricCredential = (ok, payload, errorCode) => {
    const parsed = parseUnlockResult(ok, payload, errorCode);
    settleAll(unlockQueue, parsed);
  };
}

function invokeAndroid(methodName, args) {
  try {
    const bridge = window.Jph;
    if (!bridge || typeof bridge[methodName] !== 'function') {
      return { ok: false, error: `${methodName} no disponible.` };
    }
    bridge[methodName](...args);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: String(err?.message || err) };
  }
}

function invokeIos(action, params) {
  try {
    const handler = window.webkit?.messageHandlers?.JphIOS;
    if (!handler || typeof handler.postMessage !== 'function') {
      return { ok: false, error: 'JphIOS no disponible.' };
    }
    handler.postMessage({ action, params: params || {} });
    return { ok: true };
  } catch (err) {
    return { ok: false, error: String(err?.message || err) };
  }
}

function withTimeout(queue, runNative, timeoutCode) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId = null;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      if (timeoutId != null) clearTimeout(timeoutId);
      fn(value);
    };

    const resolver = (value) => finish(resolve, value);
    queue.push(resolver);
    timeoutId = setTimeout(() => {
      const idx = queue.indexOf(resolver);
      if (idx >= 0) queue.splice(idx, 1);
      finish(reject, Object.assign(new Error(timeoutCode), { code: timeoutCode }));
    }, TIMEOUT_MS);

    const started = runNative();
    if (!started.ok) {
      const idx = queue.indexOf(resolver);
      if (idx >= 0) queue.splice(idx, 1);
      finish(
        reject,
        Object.assign(new Error(started.error || BRIDGE_CODES.BRIDGE_UNAVAILABLE), {
          code: BRIDGE_CODES.BRIDGE_UNAVAILABLE,
        })
      );
    }
  });
}

/**
 * Persiste secreto tras login password exitoso (best-effort).
 * @param {string} email
 * @param {'password'|'refresh'} secretType
 * @param {string} secret
 * @returns {Promise<{ ok: boolean, code?: string, message?: string }>}
 */
export async function saveTigerBiometricCredential(email, secretType, secret) {
  ensureTigerBiometricCredentialBridge();
  const normalized = normalizeEmail(email);
  const type = normalizeSecretType(secretType);
  const value = typeof secret === 'string' ? secret : '';

  if (!isValidEmailShape(normalized) || !type || !value || value.length > MAX_SECRET_LEN) {
    return { ok: false, code: BRIDGE_CODES.INVALID_PAYLOAD, message: 'Datos inválidos para guardar biometría.' };
  }
  if (!isTigerEnvironment()) {
    return { ok: false, code: BRIDGE_CODES.WEB_SKIP };
  }
  if (!isTigerBiometricCredentialBridgeAvailable()) {
    return { ok: false, code: BRIDGE_CODES.BRIDGE_UNAVAILABLE };
  }

  if (isTigerSimActive() && !isTigerAndroid() && !isTigerIOS()) {
    const store = readSimStore();
    store[normalized] = { secret_type: type, secret: value, saved_at: Date.now() };
    writeSimStore(store);
    return { ok: true };
  }

  try {
    const result = await withTimeout(
      saveQueue,
      () => {
        if (isTigerAndroid()) {
          return invokeAndroid('tigerSaveBiometricCredential', [normalized, type, value]);
        }
        return invokeIos('tigerSaveBiometricCredential', {
          email: normalized,
          secret_type: type,
          secret: value,
        });
      },
      BRIDGE_CODES.TIMEOUT
    );
    if (!result.ok) {
      return {
        ok: false,
        code: BRIDGE_CODES.SAVE_FAILED,
        message: redactSecretsInMessage(result.description) || 'No se pudo guardar la biometría en el dispositivo.',
      };
    }
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      code: err?.code || BRIDGE_CODES.SAVE_FAILED,
      message: redactSecretsInMessage(err?.message) || 'No se pudo guardar la biometría en el dispositivo.',
    };
  }
}

/**
 * Biometría OS + lectura Keystore → secreto para Auth.
 * @param {string} email
 */
export async function unlockTigerBiometricCredential(email) {
  ensureTigerBiometricCredentialBridge();
  const normalized = normalizeEmail(email);

  if (!isValidEmailShape(normalized)) {
    return {
      ok: false,
      code: BRIDGE_CODES.INVALID_PAYLOAD,
      message: 'Ingresá un email válido.',
      email: normalized,
      secretType: null,
      secret: '',
    };
  }
  if (!isTigerEnvironment()) {
    return {
      ok: false,
      code: BRIDGE_CODES.WEB_SKIP,
      message: null,
      email: normalized,
      secretType: null,
      secret: '',
    };
  }
  if (!isTigerBiometricCredentialBridgeAvailable()) {
    return {
      ok: false,
      code: BRIDGE_CODES.BRIDGE_UNAVAILABLE,
      message:
        'Huella no disponible en esta app. Ingresá con usuario y contraseña.',
      email: normalized,
      secretType: null,
      secret: '',
    };
  }

  if (isTigerSimActive() && !isTigerAndroid() && !isTigerIOS()) {
    const entry = readSimStore()[normalized];
    if (!entry?.secret || !entry?.secret_type) {
      return {
        ok: false,
        code: BRIDGE_CODES.NO_SECRET,
        message: 'No hay biometría de lab guardada. Entrá una vez con contraseña (tigerSim).',
        email: normalized,
        secretType: null,
        secret: '',
      };
    }
    return assertUnlockForLogin(
      parseUnlockResult(true, {
        email: normalized,
        secret_type: entry.secret_type,
        secret: entry.secret,
      }),
      normalized
    );
  }

  try {
    const raw = await withTimeout(
      unlockQueue,
      () => {
        if (isTigerAndroid()) {
          return invokeAndroid('tigerUnlockBiometricCredential', [normalized]);
        }
        return invokeIos('tigerUnlockBiometricCredential', { email: normalized });
      },
      BRIDGE_CODES.TIMEOUT
    );
    return assertUnlockForLogin(raw, normalized);
  } catch (err) {
    if (err?.code === BRIDGE_CODES.TIMEOUT) {
      return {
        ok: false,
        code: BRIDGE_CODES.TIMEOUT,
        message: 'Tiempo agotado esperando biometría del dispositivo.',
        email: normalized,
        secretType: null,
        secret: '',
      };
    }
    return {
      ok: false,
      code: err?.code || BRIDGE_CODES.BRIDGE_UNAVAILABLE,
      message: redactSecretsInMessage(err?.message) || 'No se pudo iniciar biometría.',
      email: normalized,
      secretType: null,
      secret: '',
    };
  }
}

/**
 * @param {string} [email] — si omite, limpia todas (sim) o pide clear global al nativo si soporta
 */
export async function clearTigerBiometricCredential(email) {
  ensureTigerBiometricCredentialBridge();
  const normalized = email != null && String(email).trim() ? normalizeEmail(email) : '';

  if (!isTigerEnvironment()) {
    return { ok: false, code: BRIDGE_CODES.WEB_SKIP };
  }

  if (isTigerSimActive() && !isTigerAndroid() && !isTigerIOS()) {
    if (!normalized) {
      try {
        sessionStorage.removeItem(SIM_STORE_KEY);
      } catch {
        /* ignore */
      }
      return { ok: true };
    }
    const store = readSimStore();
    delete store[normalized];
    writeSimStore(store);
    return { ok: true };
  }

  if (!isTigerBiometricCredentialBridgeAvailable()) {
    return { ok: false, code: BRIDGE_CODES.BRIDGE_UNAVAILABLE };
  }

  try {
    const result = await withTimeout(
      clearQueue,
      () => {
        if (isTigerAndroid()) {
          return invokeAndroid('tigerClearBiometricCredential', [normalized || '']);
        }
        return invokeIos('tigerClearBiometricCredential', { email: normalized || '' });
      },
      BRIDGE_CODES.TIMEOUT
    );
    if (!result.ok) {
      return {
        ok: false,
        code: BRIDGE_CODES.CLEAR_FAILED,
        message: redactSecretsInMessage(result.description) || 'No se pudo borrar la biometría del dispositivo.',
      };
    }
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      code: err?.code || BRIDGE_CODES.CLEAR_FAILED,
      message: redactSecretsInMessage(err?.message),
    };
  }
}

/**
 * @param {string} email
 * @returns {Promise<boolean>}
 */
export async function hasTigerBiometricCredential(email) {
  ensureTigerBiometricCredentialBridge();
  const normalized = normalizeEmail(email);
  if (!isValidEmailShape(normalized) || !isTigerEnvironment()) return false;

  if (isTigerSimActive() && !isTigerAndroid() && !isTigerIOS()) {
    return Boolean(readSimStore()[normalized]?.secret);
  }

  if (isTigerAndroid() && typeof window.Jph?.tigerHasBiometricCredential === 'function') {
    try {
      const result = await withTimeout(
        hasQueue,
        () => invokeAndroid('tigerHasBiometricCredential', [normalized]),
        BRIDGE_CODES.TIMEOUT
      );
      return Boolean(result.ok && result.has);
    } catch {
      return false;
    }
  }

  if (isTigerIOS()) {
    try {
      const result = await withTimeout(
        hasQueue,
        () => invokeIos('tigerHasBiometricCredential', { email: normalized }),
        BRIDGE_CODES.TIMEOUT
      );
      return Boolean(result.ok && result.has);
    } catch {
      return false;
    }
  }

  return false;
}

export {
  BRIDGE_CODES,
  SECRET_TYPES,
  SECRET_TYPE_PASSWORD,
  SECRET_TYPE_REFRESH,
};
